interface MarketBoostEventRow {
  readonly eventId: string;
  readonly boosterPubkey: string;
  readonly amount: string;
  readonly timestamp: string;
}

const EMPTY_ROW: MarketBoostEventRow = {
  eventId: '',
  boosterPubkey: '',
  amount: '0',
  timestamp: '',
};

const CPI_INDEX_BITS = 60n;
const TX_INDEX_BITS = 16n;
const CPI_INDEX_MASK = (1n << CPI_INDEX_BITS) - 1n;
const TX_INDEX_MASK = (1n << TX_INDEX_BITS) - 1n;

function eventIdFromSequence(raw: unknown): string {
  if (!(raw instanceof Uint8Array) || raw.length !== 15 || ((raw[0] ?? 0) & 0xf0) !== 0) {
    return '';
  }

  let packed = 0n;
  for (const byte of raw) packed = (packed << 8n) | BigInt(byte);

  const cpiIndex = packed & CPI_INDEX_MASK;
  const txIndex = (packed >> CPI_INDEX_BITS) & TX_INDEX_MASK;
  const slot = packed >> (CPI_INDEX_BITS + TX_INDEX_BITS);
  return `${slot}:${txIndex}:${cpiIndex}`;
}

function amountD6String(raw: unknown): string | null {
  if (typeof raw === 'bigint') return raw >= 0n ? raw.toString() : null;
  if (typeof raw === 'number' && Number.isSafeInteger(raw) && raw >= 0) return String(raw);
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return /^\d+$/.test(trimmed) ? trimmed : null;
}

function timestampIso(raw: unknown): string | null {
  const value = typeof raw === 'bigint' ? Number(raw) : Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;
  const timestamp = new Date(value > 1e12 ? value : value * 1000);
  return Number.isNaN(timestamp.getTime()) ? null : timestamp.toISOString();
}

export function buildMarketBoostEventRow(
  raw: Record<string, unknown>,
  subject: string,
  args?: Record<string, unknown>,
): MarketBoostEventRow {
  if (args?.view !== 'events') return EMPTY_ROW;

  const subjectMarket = subject.split('.')[2];
  if (typeof args.pubkey !== 'string' || subjectMarket !== args.pubkey) return EMPTY_ROW;

  const eventId = eventIdFromSequence(raw.sequenceId);
  const boosterPubkey = typeof raw.actor === 'string' ? raw.actor : '';
  const amount = amountD6String(raw.amountD6);
  const timestamp = timestampIso(raw.ts);
  if (eventId === '' || boosterPubkey === '' || amount === null || timestamp === null) {
    return EMPTY_ROW;
  }

  return { eventId, boosterPubkey, amount, timestamp };
}
